/**
 * @file        src/layout/Topbar.jsx
 * @description Top bar: hamburger, search, cash balance pill, notifications, avatar.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-27 Claude — Role label now reflects the actual logged-in role
 *                          (super_admin / tenant_admin) instead of defaulting
 *                          to "Super Admin" whenever `user` was falsy/loading.
 *   - 2026-07-30 Claude — The pill now shows the same live supplier balance as
 *                          the dashboard KPI ("NA" for a super_admin), via the
 *                          shared useWalletBalance hook. This also drops the
 *                          /dashboard/summary query that ran on EVERY page just
 *                          to feed this one pill — the dashboard fetches its own.
 *   - 2026-08-03 Claude — Hide the global search and balance pill for a
 *                          super_admin: both are tenant-scoped features (the
 *                          /search API is tenant_admin-only and the pill can
 *                          only ever read "NA" on the platform host).
 *   - 2026-08-04 Claude — Role label covers 'staff' too (was falling through
 *                          to "—" for anyone logged in via Staff Management).
 *   - 2026-08-05 Claude — Persistent Email Settings status pill: fetches
 *                          /api/email-settings/status (low-urgency background
 *                          health check, 60s staleTime — not hammered), shows
 *                          the WORST status across every configured row
 *                          (failed > unverified > connected), and navigates to
 *                          the Email Settings page on click. Tenant-scoped
 *                          like the balance pill, so hidden for a super_admin.
 *   - 2026-09-21 Claude — The Cash Balance pill opens the supplier breakdown
 *                          (Deposit / Credit / Dues), the same three figures
 *                          TripJack's own header shows. The pill itself still
 *                          reads the deposit figure, unchanged. Click-toggled
 *                          rather than hover-only so it works on touch and from
 *                          the keyboard; Escape and an outside click close it.
 *   - 2026-09-25 Claude — This popover is now the ONLY place the supplier's
 *                          three figures are stated: the Dashboard panel that
 *                          repeated them was removed, and with it
 *                          SupplierBalances' 'strip' variant, so the prop is
 *                          gone from this call.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import GlobalSearch from '../components/GlobalSearch';
import SupplierBalances from '../components/SupplierBalances';
import { useAuth } from '../lib/auth.store';
import { useWalletBalance } from '../lib/useWalletBalance';
import { qk } from '../api/queryKeys';
import { getEmailSettingsStatus, unwrapList } from '../api/emailSettings';
import logoMark from '../assets/logo-mark.png';

const STATUS_RANK = { failed: 0, unverified: 1, connected: 2 };
const STATUS_LABEL = { failed: 'Not Connected', unverified: 'Unverified', connected: 'Connected' };
const STATUS_DOT = { failed: 'failed', unverified: 'unverified', connected: 'live' };

/** Worst status across every configured row — none configured reads as "unverified". */
function worstEmailStatus(rows) {
  if (!rows.length) return 'unverified';
  return rows.reduce((worst, r) => (STATUS_RANK[r.status] < STATUS_RANK[worst] ? r.status : worst), 'connected');
}

export default function Topbar({ onToggleSidebar }) {
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  // Shares a query key with the dashboard KPI — one fetch, two renderings.
  const wallet = useWalletBalance();
  const [balancesOpen, setBalancesOpen] = useState(false);
  const balancesRef = useRef(null);

  // Close on an outside click or Escape. Bound only while open, so the topbar
  // adds no document-level listeners in its resting state.
  useEffect(() => {
    if (!balancesOpen) return undefined;
    const onDown = (e) => { if (!balancesRef.current?.contains(e.target)) setBalancesOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setBalancesOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [balancesOpen]);

  const name = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : 'Neon Holidays';
  const isSuperAdmin = user?.role === 'super_admin';
  const role = isSuperAdmin ? 'Super Admin'
    : user?.role === 'tenant_admin' ? 'Tenant Admin'
    : user?.role === 'staff' ? 'Staff'
    : '—';

  // Email delivery health — tenant-scoped, same as the cash balance pill.
  const { data: emailStatusRaw } = useQuery({
    queryKey: qk.emailSettings.status,
    queryFn: getEmailSettingsStatus,
    enabled: !isSuperAdmin,
    staleTime: 60_000,
  });
  const emailRows = unwrapList(emailStatusRaw);
  const emailStatus = worstEmailStatus(emailRows);

  return (
    <header className="topbar glass">
      <button className="icon-btn ghost" onClick={onToggleSidebar} aria-label="Toggle menu">
        <Icon name="bars" />
      </button>
      {!isSuperAdmin && <GlobalSearch />}
      <div className="top-actions">
        {!isSuperAdmin && (
        <div className="balance-pill-wrap" ref={balancesRef}>
          <button
            type="button"
            className="balance-pill"
            style={{ cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
            onClick={() => setBalancesOpen((o) => !o)}
            aria-expanded={balancesOpen}
            aria-label="Cash Balance — show supplier deposit, credit and dues"
          >
            <span className="dot live" />
            <div>
              <small>Cash Balance</small>
              {/* The DEPOSIT figure, exactly as before. The credit line and dues
                  behind it are in the panel below, not folded into this number. */}
              <b>{wallet.isLoading ? 'Loading…' : wallet.display}</b>
            </div>
            <Icon name="chev" size={14} />
          </button>
          {balancesOpen && (
            <div className="sup-bal-pop">
              <div className="sup-bal-pop-head">Supplier account · TripJack</div>
              <SupplierBalances />
            </div>
          )}
        </div>
        )}
        {!isSuperAdmin && (
        <button
          type="button"
          className="balance-pill"
          style={{ cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
          onClick={() => navigate('/account/email-settings')}
          aria-label="Email delivery status — go to Email Settings"
        >
          <span className={`dot ${STATUS_DOT[emailStatus]}`} />
          <div>
            <small>Email</small>
            <b>{STATUS_LABEL[emailStatus]}</b>
          </div>
        </button>
        )}
        <button className="icon-btn notif" aria-label="Notifications">
          <Icon name="bell" />
        </button>
        <div className="avatar">
          <img src={logoMark} alt="profile" />
          <div className="who">
            <b>{name}</b>
            <small>{role}</small>
          </div>
        </div>
      </div>
    </header>
  );
}
